"use client"

import { pickPostImageFormat } from "@/(admin)/modules/ui-builder/config/lib/post-helpers"
import { toImageSrc } from "@/lib/utils"
import { NotificationListContentProps, PostData } from "@/types/types"
import Image from "next/image"
import Link from "next/link"

import PostLink from "@/components/PostLink"
import { getFixedT } from "@/languages/i18n"
import { DefaultImage } from "@/lib/external-components"
import { NotificationListSkeleton } from "./NotificationListSkeleton"

export function NotificationListContent({
  id,
  title = "Thông báo",
  viewAllLink,
  posts,
  loading,
  className = "",
  locale,
}: NotificationListContentProps) {
  const t = getFixedT(locale)
  const validPosts = posts?.filter((post: PostData) => post && post.id) || []

  if (loading) {
    return <NotificationListSkeleton className={className} />
  }

  if (validPosts.length === 0) {
    return null
  }

  return (
    <div id={id} className={className}>
      <div className="container max-w-full sm:max-w-[1304px] mx-auto px-4 sm:px-2 py-8 sm:pt-8 sm:pb-0">
        <div className="flex items-center justify-between pb-3 sm:pb-1">
          <h2 className="text-lg sm:text-2xl sm:leading-[2.325rem] font-bold leading-[1.74375rem] text-[#333333]">
            {title}
          </h2>
          {viewAllLink && (
            <Link
              href={viewAllLink}
              className="text-[#19B04E] hover:text-[#19B04E]/80 text-sm sm:text-base font-normal leading-[1.55]"
            >
              {t("View all")}
            </Link>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-0">
          {validPosts.map((post: PostData) => {
            const imageUrl = pickPostImageFormat(post, "medium") || pickPostImageFormat(post, "thumbnail")
            const imageSrc = imageUrl ? toImageSrc(imageUrl) : null

            const postSlug = post.attributes?.slug || post.id.toString()
            const postTitle = post.attributes?.title || post.attributes?.name || ""
            const postId = post.id
            const currentViews = post.attributes?.views
            return (
              <div key={post.id} className="p-2 sm:pb-9 pb-5">
                <PostLink
                  href={`/${postSlug}`}
                  postId={postId}
                  className="flex flex-col group w-full"
                  aria-label={postTitle}
                >
                  <div className="relative w-full aspect-[163.5/200] sm:aspect-[700/386] overflow-hidden rounded-md mb-3 mx-auto transition duration-300 group-hover:brightness-110">
                    <Image
                      src={imageSrc || DefaultImage}
                      alt={postTitle}
                      fill
                      className="object-cover transition-transform duration-300 hover:scale-105"
                      sizes="(max-width: 640px) 50vw, 50vw"
                    />
                  </div>
                  <h3 className="text-base sm:text-xl font-semibold sm:font-bold text-[#333333] sm:leading-[1.9375rem] transition-colors duration-300 leading-[1.55rem] sm:mb-1 mb-0 group-hover:text-[#19B04E] group-hover:brightness-125">
                    {postTitle}
                  </h3>
                  <p className="text-[11px] sm:text-xs lg:text-sm text-[#111827] leading-snug line-clamp-2">
                    {post.attributes?.excerpt || post.attributes?.shortDescription || ""}
                  </p>
                </PostLink>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
